import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReferralStats } from '../../actions';
import { api } from '../../services/apiService';
import { isUpdateApiFailure } from '../../sagas/apiFailure';
import { referralRewardsAvailable } from '../../utils/native-referral-policy';
import { isNativeCapacitorRuntime } from '../../services/revenueCatService';
import { isMobileDevPaywallEnabled } from '../../constants/mobileBilling';
import { trackDailyLimit, trackPaywallEntry } from '../../utils/tracking';
import {
  limitCopy,
  practiceEmailOptInEnabled,
  resetPhrase,
  shareCtaLabel,
  trialCtaLabel,
} from './daily-limit';
import { readStorage, useTrialOffer } from './use-daily-limit';
import './daily-limit.scss';

const EMAIL_SAVED = "You're set. We'll email you tomorrow's questions.";
const EMAIL_FAILED = "That didn't save. Please try again.";

/**
 * What a student sees once today's question allowance is used up: when the next
 * set unlocks (in their own clock), the Pro trial when they can take it (else
 * "See plans"), an invite where the caller has a share window, and (flag-gated)
 * a practice-email opt-in.
 *
 * Rendered as a card high on the quiz results page (variant "card") and inside
 * the Create Quiz modal (variant "modal", see DailyLimitModal). The words are the
 * same in both places. Variant "actions" is the button row alone, for the first
 * quiz's block on the results page (FirstQuizCommitment), which says when the
 * next set unlocks and offers its own reminder, so neither is repeated here.
 *
 * Props:
 * - surface: 'exam_result' | 'create_quiz', for analytics only.
 * - info: resolveDailyLimit's result ({ limit, free, exam }).
 * - onSeePlans(planId): go to the subscriptions page (trial and plans buttons both go
 *   there). planId is the web trial plan to open at its review step, or null.
 * - onShare: optional; adds the invite button, which calls it (Create Quiz opens its
 *   share window). The results page passes none: its invite prompt sits right
 *   under the card, and two share buttons a few lines apart read as a mistake.
 * - onClose: optional; adds a Close button (modal), shown even while the trial
 *   check runs.
 * - shownRef: optional ref that outlives this panel, so daily_limit_shown fires
 *   once per page view even when the caller mounts the panel again (Create Quiz
 *   re-opens its modal on every count response).
 * - now: optional Date, for tests.
 */
export default function DailyLimitPanel({
  surface, info, onSeePlans, onShare, onClose, variant = 'card', now, titleId, shownRef,
}) {
  const dispatch = useDispatch();
  const userId = readStorage('userId');
  const limit = info?.limit ?? null;

  // A trial can only be offered to a free student with a real allowance. Paid
  // plans (free === false) are never checked, so they never see trial words.
  const trialPossible = info?.free !== false && Number.isInteger(limit) && limit > 0;
  const trial = useTrialOffer({ enabled: trialPossible, userId, exam: info?.exam ?? null });
  // The server only offers a trial to an account that never held a non-FREE
  // plan, so an eligible student whose plan read failed is on the free plan.
  const free = info?.free === true || (info?.free === null && trial.days !== null);
  const ready = trial.settled;

  // iOS ruling B5: the native app offers no referral reward, so no invite there.
  const shareAvailable = Boolean(onShare) && referralRewardsAvailable();
  const stats = useSelector((state) => state.referral?.stats);
  const statsRequested = useRef(false);
  useEffect(() => {
    if (ready && shareAvailable && !stats && !statsRequested.current) {
      statsRequested.current = true;
      dispatch(fetchReferralStats());
    }
  }, [ready, shareAvailable, stats, dispatch]);

  const [email, setEmail] = useState('idle');
  const emailOptIn = practiceEmailOptInEnabled();

  const eventData = {
    surface,
    plan: free ? 'free' : 'other',
    offer: trial.days !== null ? 'trial' : 'plans',
    // The runtime, not the trial check's answer: paid plans never run that check.
    native: isNativeCapacitorRuntime() || isMobileDevPaywallEnabled(),
  };
  const ownShownRef = useRef(false);
  const shownLogged = shownRef || ownShownRef;
  useEffect(() => {
    if (ready && !shownLogged.current) {
      shownLogged.current = true;
      try {
        trackDailyLimit('daily_limit_shown', eventData);
      } catch {
        // Tracking is best-effort.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const copy = limitCopy({ limit, free, exam: info?.exam ?? null, when: resetPhrase(now || new Date()) });
  const modal = variant === 'modal';
  const actionsOnly = variant === 'actions';

  // Analytics never stand between the student and the next page: a throwing
  // sink (a consent manager's dataLayer.push) is swallowed and the action runs.
  const tracked = (track, action) => {
    try {
      track();
    } catch {
      // Tracking is best-effort.
    } finally {
      action();
    }
  };

  const onPrimary = () => tracked(() => {
    const offerTrial = trial.days !== null;
    trackDailyLimit(offerTrial ? 'daily_limit_trial_clicked' : 'daily_limit_plans_clicked', eventData);
    // The existing paywall entry funnel, so this entry point shows up there too.
    trackPaywallEntry('paywall_entry_clicked', { source: `daily_limit_${surface}` });
  }, () => {
    // A web trial goes straight to its plan's review step; otherwise the plan list.
    const planId = trial.days !== null && !trial.native ? (trial.planId ?? null) : null;
    onSeePlans(planId);
  });

  const onInvite = () => tracked(() => trackDailyLimit('daily_limit_share_clicked', eventData), onShare);

  const onEmail = async () => {
    try {
      trackDailyLimit('daily_limit_email_clicked', eventData);
    } catch {
      // Tracking is best-effort.
    }
    setEmail('saving');
    let saved = false;
    try {
      const res = await api.setPreference({ userId, body: { emailPracticeQuestions: true } });
      saved = Boolean(res) && !isUpdateApiFailure(res);
    } catch {
      saved = false;
    }
    setEmail(saved ? 'saved' : 'failed');
  };

  const Title = modal ? (
    <Typography variant="h5" className="header daily-limit-title" id={titleId}>{copy.title}</Typography>
  ) : (
    <Typography component="h2" className="daily-limit-title" id={titleId}>{copy.title}</Typography>
  );

  return (
    <>
      {!actionsOnly && Title}
      {!actionsOnly && <Typography variant="body2" className="daily-limit-body">{copy.body}</Typography>}
      {/* Close sits in the same row whether or not the trial check is done, so the
          student can always leave. */}
      <Box className={ready ? 'daily-limit-actions' : 'daily-limit-actions daily-limit-pending'}>
        {!ready ? (
          <Box role="status" aria-label="Loading options" className="daily-limit-spinner">
            <CircularProgress size={20} aria-hidden="true" />
          </Box>
        ) : (
          <>
            <Button variant="contained" color="primary" onClick={onPrimary}>
              {trial.days !== null ? trialCtaLabel({ days: trial.days, native: trial.native }) : 'See plans'}
            </Button>
            {shareAvailable && (
              <Button variant="outlined" color="primary" onClick={onInvite}>
                {shareCtaLabel({ free, stats })}
              </Button>
            )}
            {emailOptIn && !actionsOnly && userId && email !== 'saved' && (
              <Button variant="text" color="primary" onClick={onEmail} disabled={email === 'saving'}>
                Email me tomorrow's questions
              </Button>
            )}
          </>
        )}
        {onClose && (
          <Button variant="text" className="daily-limit-close" onClick={onClose}>Close</Button>
        )}
      </Box>
      {email === 'saved' && (
        <Typography variant="body2" className="daily-limit-note" role="status">{EMAIL_SAVED}</Typography>
      )}
      {email === 'failed' && (
        <Typography variant="body2" className="daily-limit-note" role="alert">{EMAIL_FAILED}</Typography>
      )}
    </>
  );
}
